import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQueryClient } from 'react-query'
import { SnackbarContext } from '../../../providers/SnackbarContext'

import { Stack, Button, Divider } from '@mui/material'
import Loading from '../../../components/Loading/Loading.component'
import DetailInput from '../../../components/DetailInput/DetailInput.component'
import { useAppNavigate } from '../../../utils/url'
import { TrailSteward } from '../../../models/TrailSteward.model'
import { TrailStewardAddEvent } from '../../../events/TrailStewardAddEvent'
import { TrailStewardUpdateEvent } from '../../../events/TrailStewardUpdateEvent'
import { TrailStewardService } from '../../../services/TrailStewardService/TrailStewardService'
import { AppStateContext } from '../../../providers/AppStateContext'
import SectionedForm, { FormSection } from '../../../components/Sections/SectionedForm.component'
import SectionSubheading from '../../../components/Sections/SectionSubheading.component'
import { TrailStewardSectionIds, trailStewardSections } from './TrailStewardSections'

export type TrailStewardFormProps = {
  trailSteward?: TrailSteward
  onAdd?: (trailSteward: TrailSteward) => void
  onEdit?: (trailSteward: TrailSteward) => void
  onCancel: () => void
  saveTrigger?: number
  disableRedirect?: boolean
  // section of the form to scroll to when it opens (see TrailStewardSectionIds)
  initialSection?: string
}
const TrailStewardForm = (props: TrailStewardFormProps) => {
  const { trailSteward, onCancel, onAdd, onEdit, saveTrigger, disableRedirect = false, initialSection } = props

  const navigate = useAppNavigate()

  const [tempTrailSteward, setTempTrailSteward] = useState<
    Partial<TrailSteward> & {
      trailId?: number
      systemId?: number
    }
  >(
    {
      ...trailSteward,
    } || {},
  )

  const [isSubmitting, setIsSubmitting] = useState(false)
  const queryClient = useQueryClient()

  const trailStewardService = useMemo(() => new TrailStewardService(), []) // memo to prevent re-creating service on every render

  const addTrailSteward = useMutation(
    (mutationParams: { trailSteward: TrailStewardAddEvent }) => {
      setIsSubmitting(true)
      return trailStewardService.add(mutationParams.trailSteward)
    },
    {
      onSuccess: (result) => {
        SnackbarContext.show('Trail Steward added successfully!')

        if (!disableRedirect) {
          navigate(`/trail-steward/${result.id}`)
          queryClient.removeQueries(['TrailStewardService.search'])
        }
        queryClient.invalidateQueries()
        setIsSubmitting(false)

        if (typeof onAdd === 'function') {
          onAdd(result)
        }
      },
      onError: (err) => {
        SnackbarContext.show(`Trail steward failed to add: ${err}`, 'error')
        console.error(err)
        setIsSubmitting(false)
      },
    },
  )

  const updateSponsor = useMutation(
    (mutationParams: { id: number; changedProperties: TrailStewardUpdateEvent }) => {
      setIsSubmitting(true)
      return trailStewardService.update(mutationParams.id, mutationParams.changedProperties)
    },
    {
      onSuccess: (result) => {
        SnackbarContext.show('Trail steward updated successfully!')
        queryClient.invalidateQueries()
        setIsSubmitting(false)

        if (typeof onEdit === 'function') {
          onEdit(result)
        }
      },
      onError: (err) => {
        SnackbarContext.show(`Trail steward failed to update: ${err}`, 'error')
        console.error(err)
        setIsSubmitting(false)
      },
    },
  )

  const handleSubmit = () => {
    if (!tempTrailSteward.name) {
      SnackbarContext.show(`Trail steward's name required`, 'error')
      return
    }

    setIsSubmitting(true)

    if (!trailSteward) {
      // format the data to match the API
      const formattedSponsor: TrailStewardAddEvent = {
        name: tempTrailSteward.name,
        phone: tempTrailSteward.phone,
        email: tempTrailSteward.email,
        region: AppStateContext.getRegion(),
      }

      // adding a new record
      addTrailSteward.mutate({ trailSteward: formattedSponsor })
    } else {
      // identify the properties that have changed
      const didChange = (field: keyof TrailSteward) => {
        return tempTrailSteward[field] !== undefined && tempTrailSteward[field] !== trailSteward[field]
      }

      const changedProperties: TrailStewardUpdateEvent = {
        id: trailSteward.id,
        name: didChange('name') ? tempTrailSteward.name : undefined,
        phone: didChange('phone') ? tempTrailSteward.phone : undefined,
        email: didChange('email') ? tempTrailSteward.email : undefined,
      }

      // edit an existing record
      if (!Object.keys(changedProperties).filter((key) => key !== 'id').length) {
        // nothing changed so just close the dialog
        onCancel()
      } else {
        updateSponsor.mutate({ id: trailSteward.id, changedProperties })
      }
    }
  }

  useEffect(() => {
    if (saveTrigger) {
      // if outside consumer triggered save, then submit
      handleSubmit()
    }
  }, [saveTrigger])

  const handleCancel = () => {
    if (typeof onCancel === 'function') {
      onCancel()
    }
  }

  const keyDownSubmit = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit()
    }
  }

  return (
    <>
      {isSubmitting && <Loading sx={{ py: 20 }} />}
      {!isSubmitting && (
        <>
          <SectionedForm sections={trailStewardSections} initialSection={initialSection}>
            {/* --- General ---------------------------------------------------- */}
            <FormSection id={TrailStewardSectionIds.general}>
              <DetailInput<TrailSteward>
                label="Name"
                field="name"
                tempDetail={tempTrailSteward}
                setTempDetail={setTempTrailSteward}
                keyDownSubmit={keyDownSubmit}
              />

              <Divider />
              <SectionSubheading title="Contact" />
              <Stack direction={{ zero: 'column', sm: 'row' }} spacing={2.5}>
                <DetailInput<TrailSteward>
                  label="Phone"
                  field="phone"
                  tempDetail={tempTrailSteward}
                  setTempDetail={setTempTrailSteward}
                  keyDownSubmit={keyDownSubmit}
                />
                <DetailInput<TrailSteward>
                  label="Email"
                  field="email"
                  tempDetail={tempTrailSteward}
                  setTempDetail={setTempTrailSteward}
                  keyDownSubmit={keyDownSubmit}
                />
              </Stack>
            </FormSection>
          </SectionedForm>
          <Stack direction="row" justifyContent="end" alignItems="center" spacing={2}>
            <Button variant="outlined" onClick={handleCancel}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSubmit}>
              Save
            </Button>
          </Stack>
        </>
      )}
    </>
  )
}
export default TrailStewardForm
